import { createReducer } from '@reduxjs/toolkit'

import {
  IMetric,
  IMetricRawData,
  TDefinitionSlice,
  THeadlineMetric
} from 'types/GlobalMetric'
import { getMetricDataKey } from 'utils/metric'

import * as actions from 'redux/actions/Metrics/actions'
import * as authenticationActions from 'redux/actions/Authentication/actions'
import * as globalFilterActions from 'redux/actions/GlobalFilter/actions'
import * as kpiOptionActions from 'redux/actions/KpiOptions/actions'
import * as sliceActions from 'redux/actions/Slices/actions'
import * as widgetActions from 'redux/actions/Widgets/actions'
import { insertItems, removeItem, upsertItem } from 'redux/utils'

export interface MetricReducerType {
  data: {
    [id: string]: IMetric
  }

  /**
   * The metric the results page leads with. `resolved` tells "no headline
   * for this customer" from "not answered yet".
   */
  headline: {
    data: THeadlineMetric | null
    resolved: boolean
  }
  rawData: {
    [id: string]: {
      [sliceKey: string]: {
        data: IMetricRawData | null
        error: boolean
        // the skärningar a failed request said are not configured
        missingSlices: TDefinitionSlice[]
      }
    }
  }
  // In-flight raw data requests, keyed like rawData. Lets tryFetchMetricData
  // skip duplicate requests for a slice that is already being fetched.
  fetchingRawData: {
    [id: string]: {
      [sliceKey: string]: boolean
    }
  }
  fetched: boolean
  fetching: boolean
}

// Initial state
const INITIAL_STATE: MetricReducerType = {
  data: {},
  headline: {
    data: null,
    resolved: false
  },
  rawData: {},
  fetchingRawData: {},
  fetched: false,
  fetching: false
}

// Reducer
const metricReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getAll, (state) => {
      state.fetched = false
      state.fetching = true
    })
    .addCase(actions.getAllSuccess, (state, action) => {
      const { payload } = action

      state.fetched = true
      state.fetching = false
      state.data = insertItems(payload)
    })
    .addCase(actions.getHeadlineSuccess, (state, action) => {
      state.headline = { data: action.payload, resolved: true }
    })
    .addCase(actions.getHeadlineFailed, (state) => {
      state.headline = { data: null, resolved: true }
    })
    .addCase(actions.updateSuccess, (state, action) => {
      const { payload } = action

      state.data = upsertItem(state.data, payload)
    })
    .addCase(actions.createSuccess, (state, action) => {
      state.data = upsertItem(state.data, action.payload)
    })
    .addCase(actions.deleteSuccess, (state, action) => {
      state.data = removeItem(state.data, action.payload)
    })
    .addCase(actions.getOneRaw, (state, action) => {
      const { id, body } = action.payload

      if (!state.fetchingRawData[id]) {
        state.fetchingRawData[id] = {}
      }

      state.fetchingRawData[id][getMetricDataKey(body)] = true
    })
    .addCase(actions.getOneRawSuccess, (state, action) => {
      const { data, id, body } = action.payload
      const dataKey = getMetricDataKey(body)

      if (!state.rawData[id]) {
        state.rawData[id] = {}
      }

      state.rawData[id][dataKey] = {
        data,
        error: false,
        missingSlices: []
      }

      if (state.fetchingRawData[id]) {
        state.fetchingRawData[id][dataKey] = false
      }
    })
    .addCase(actions.getOneRawFailed, (state, action) => {
      const { id, body, missingSlices } = action.payload
      const dataKey = getMetricDataKey(body)

      if (!state.rawData[id]) {
        state.rawData[id] = {}
      }

      state.rawData[id][dataKey] = {
        data: null,
        error: true,
        missingSlices
      }

      if (state.fetchingRawData[id]) {
        state.fetchingRawData[id][dataKey] = false
      }
    })
    .addCase(actions.putConfigSuccess, (state, action) => {
      const { payload } = action

      payload.metrics.forEach((metricId) => {
        if (state.data[metricId]) {
          state.data[metricId].config = payload
        }
      })
    })

    .addCase(globalFilterActions.update, (state) => {
      state.rawData = {}
      state.fetchingRawData = {}
    })

    // A widget or kpi option edit can change the saved filter conditions that
    // are resolved server-side by source id. Those conditions are not part of
    // the raw data cache key (only the source id is), so any data-affecting
    // edit must drop the cache to avoid serving stale segmented chart data in
    // the DataDialog. Cosmetic edits set skipFetchData and are left untouched.
    .addCase(widgetActions.updateSuccess, (state, action) => {
      if (action.payload.skipFetchData) return

      state.rawData = {}
      state.fetchingRawData = {}
    })
    .addCase(kpiOptionActions.updateSuccess, (state, action) => {
      if (action.payload.skipFetchData) return

      state.rawData = {}
      state.fetchingRawData = {}
    })

    /*
     * Editing a slice's filter conditions changes what every metric scoped by
     * it computes, and the conditions are not part of the raw data cache key.
     * EntitySliceRow already invalidates the widget cache for the same reason;
     * the metric cache needs the same treatment.
     */
    .addCase(sliceActions.updateSuccess, (state) => {
      state.rawData = {}
      state.fetchingRawData = {}
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default metricReducer
